import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BriefcaseBusiness, CheckCircle2, ClipboardCheck, Copy, ExternalLink, GraduationCap, History, Loader2, ShieldAlert, Sparkles } from "lucide-react";
import { toast } from "sonner";

const FUNCTIONAL_AREAS = ["Medical Affairs","Clinical Development","Regulatory Affairs","Drug Safety/PV","Commercial/Marketing","Research & Discovery","Manufacturing/CMC","Market Access","Medical Science Liaison","Biometrics/Data Science","Human Resources","Finance/Legal","Information Technology","Other"];
const THERAPEUTIC_AREAS = ["Oncology","Rare Disease","Neurology","Immunology","Cardiovascular","Infectious Disease","Metabolic/Endocrine","Dermatology","Respiratory","Ophthalmology","Unknown"];
const SENIORITY = ["C-Suite","VP","Senior Director","Director","Associate Director","Senior Manager","Manager","Principal/Staff IC","Senior IC","IC"];
const GEO = ["Northeast US","Mid-Atlantic US","Southeast US","Midwest US","Southwest US","West Coast US","Mountain/Plains US","Remote/Flexible"];

const clean = (value) => value === null || value === undefined || value === "" ? "—" : String(value);
const withProtocol = (value) => {
  if (!value) return "";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};
const formatRange = (item) => {
  const start = item?.start_date ? String(item.start_date).slice(0, 10) : "Unknown start";
  const end = item?.is_current ? "Present" : (item?.end_date ? String(item.end_date).slice(0, 10) : "Unknown end");
  return `${start} — ${end}`;
};

function MultiSelect({ options, value, onChange, testId }) {
  const selected = value || [];
  const toggle = (item) => onChange(selected.includes(item) ? selected.filter(v => v !== item) : [...selected, item]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map(opt => (
        <button key={opt} type="button" onClick={() => toggle(opt)} data-testid={`${testId}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${selected.includes(opt) ? "bg-evergreen-950 text-white border-evergreen-950" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
          {opt}
        </button>
      ))}
    </div>
  );
}

function SingleSelect({ options, value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={testId}>
      {options.map(opt => (
        <button key={opt} type="button" onClick={() => onChange(opt)} data-testid={`${testId}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${value === opt ? "bg-evergreen-950 text-white border-evergreen-950" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
          {opt}
        </button>
      ))}
    </div>
  );
}

function SourceActions({ raw, snapshot, anonymousId, onCopy }) {
  const linkedinUrl = raw?.url || snapshot?.linkedin_url || "";
  const employerDomain = snapshot?.current_employer_domain || raw?.latest_experience?.company_domain || raw?.experience?.find?.(item => item?.company_domain)?.company_domain || "";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="candidate-review-source-actions">
      <Button data-testid="candidate-review-open-linkedin-button" variant="outline" disabled={!linkedinUrl} onClick={() => window.open(withProtocol(linkedinUrl), "_blank", "noopener,noreferrer")}>
        <ExternalLink className="w-4 h-4 mr-2" /> Open LinkedIn Profile
      </Button>
      <Button data-testid="candidate-review-copy-linkedin-button" variant="outline" disabled={!linkedinUrl} onClick={() => onCopy(linkedinUrl, "LinkedIn URL copied")}>
        <Copy className="w-4 h-4 mr-2" /> Copy LinkedIn URL
      </Button>
      <Button data-testid="candidate-review-copy-anonymous-id-button" variant="outline" onClick={() => onCopy(anonymousId, "Anonymous ID copied")}>
        <Copy className="w-4 h-4 mr-2" /> Copy Anonymous ID
      </Button>
      <Button data-testid="candidate-review-open-employer-domain-button" variant="outline" disabled={!employerDomain} onClick={() => window.open(withProtocol(employerDomain), "_blank", "noopener,noreferrer")}>
        <ExternalLink className="w-4 h-4 mr-2" /> Open Employer Domain
      </Button>
    </div>
  );
}

function ExperienceTimeline({ experience = [] }) {
  if (!Array.isArray(experience) || experience.length === 0) {
    return <p className="text-sm text-slate-500" data-testid="candidate-review-empty-experience">No experience entries found in the raw payload.</p>;
  }
  return (
    <div className="space-y-3" data-testid="candidate-review-experience-timeline">
      {experience.map((item, index) => (
        <div key={`${item?.company || "company"}-${item?.title || "title"}-${index}`} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`candidate-review-experience-${index}`}>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
            <div>
              <p className="font-medium text-slate-900">{clean(item?.title)}</p>
              <p className="text-sm text-slate-600">{clean(item?.company)}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {item?.is_current && <Badge className="bg-lime-50 text-lime-700 border border-lime-200">Current</Badge>}
              <Badge variant="outline" className="font-mono text-xs">{formatRange(item)}</Badge>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3 text-xs text-slate-500">
            <span data-testid={`candidate-review-experience-locality-${index}`}>Location: {clean(item?.locality)}</span>
            <span data-testid={`candidate-review-experience-domain-${index}`}>Domain: {clean(item?.company_domain)}</span>
            <span data-testid={`candidate-review-experience-revenue-${index}`}>Revenue: {clean(item?.yearly_revenue_range)}</span>
          </div>
          {item?.summary && <p className="text-sm text-slate-600 mt-3 whitespace-pre-line" data-testid={`candidate-review-experience-summary-${index}`}>{item.summary}</p>}
        </div>
      ))}
    </div>
  );
}

function EducationList({ education = [] }) {
  if (!Array.isArray(education) || education.length === 0) {
    return <p className="text-sm text-slate-500" data-testid="candidate-review-empty-education">No education entries found in the raw payload.</p>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="candidate-review-education-list">
      {education.map((item, index) => (
        <div key={`${item?.school_name || "school"}-${index}`} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`candidate-review-education-${index}`}>
          <p className="font-medium text-slate-900">{clean(item?.school_name)}</p>
          <p className="text-sm text-slate-600 mt-1">{clean(item?.degree)}</p>
          <p className="text-xs text-slate-500 mt-2">{clean(item?.field_of_study)}</p>
        </div>
      ))}
    </div>
  );
}

function RawJsonPanel({ raw }) {
  return (
    <details className="rounded-lg border border-slate-200 bg-slate-950 text-slate-100 overflow-hidden" data-testid="candidate-review-raw-json-panel">
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium bg-slate-900" data-testid="candidate-review-raw-json-toggle">Raw uploaded JSON</summary>
      <pre className="p-4 text-xs overflow-x-auto max-h-[420px] whitespace-pre-wrap" data-testid="candidate-review-raw-json-content">{JSON.stringify(raw || {}, null, 2)}</pre>
    </details>
  );
}

export default function CandidateReview() {
  const { api } = useAuth();
  const [status, setStatus] = useState("pending");
  const [queue, setQueue] = useState({ items: [], total: 0, stats: {} });
  const [history, setHistory] = useState({ batches: [], stats: {} });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enriching, setEnriching] = useState(false);

  const copyText = async (value, message) => {
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        throw new Error("Clipboard API unavailable");
      }
      toast.success(message);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      textarea.style.top = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (copied) {
        toast.success(message);
      } else {
        toast.error("Copy failed — select and copy manually");
      }
    }
  };

  const loadQueue = async () => {
    setLoading(true);
    try {
      const [q, h] = await Promise.all([
        api.get(`/admin/candidate-review?status=${status}&limit=100`),
        api.get("/admin/candidate-uploads/history"),
      ]);
      setQueue(q.data);
      setHistory(h.data);
      if (!selectedId && q.data.items?.[0]) setSelectedId(q.data.items[0].anonymous_id);
    } catch {
      toast.error("Failed to load review queue");
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    if (!id) return;
    try {
      const res = await api.get(`/admin/candidate-review/${id}`);
      setDetail(res.data);
      const snap = res.data.processed_snapshot || {};
      setForm({
        current_title: snap.current_title || "",
        functional_area: snap.functional_area || "Other",
        therapeutic_areas: snap.therapeutic_areas || [],
        seniority_level: snap.seniority_level || "",
        geography_region: snap.geography_region || "",
        ai_experience_summary: snap.ai_experience_summary || "",
        intent_level10_summary: snap.intent_level10_summary || "",
        admin_notes: res.data.admin_notes || "",
      });
    } catch {
      toast.error("Failed to load candidate detail");
    }
  };

  useEffect(() => { loadQueue(); }, [status]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId]);

  const saveReview = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const res = await api.put(`/admin/candidate-review/${selectedId}`, form);
      toast.success(res.data.redaction_status === "blocked" ? "Saved, but redaction still blocked" : "Review saved");
      await loadDetail(selectedId);
      await loadQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const approveReview = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await api.post(`/admin/candidate-review/${selectedId}/approve`, form);
      toast.success("Candidate approved for client-safe search");
      await loadQueue();
      await loadDetail(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    } finally {
      setSaving(false);
    }
  };

  const runEnrichment = async () => {
    if (!selectedId) return;
    setEnriching(true);
    try {
      const res = await api.post(`/admin/candidates/${selectedId}/enrich`);
      toast.success(res.data.status === "ai_processed" ? "AI enrichment complete" : "Enrichment needs review");
      await loadQueue();
      await loadDetail(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enrichment failed");
    } finally {
      setEnriching(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="candidate-review-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 animate-fadeIn">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-amber-50 text-amber-700 border border-amber-200" data-testid="candidate-review-phase-badge">Phase 3</Badge>
            <Badge variant="outline" className="font-mono text-xs">Admin review</Badge>
          </div>
          <h1 className="text-2xl font-heading text-slate-900">Candidate Review Queue</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">Review ambiguous profiles, fix blocked summaries, and approve only client-safe candidates for search visibility.</p>
        </div>
        <div className="flex gap-2" data-testid="candidate-review-status-tabs">
          {[["pending", "Pending"], ["redaction_blocked", "Blocked"], ["failed", "Failed"], ["approved", "Approved"], ["all", "All"]].map(([val, label]) => (
            <Button key={val} data-testid={`candidate-review-tab-${val}`} variant={status === val ? "default" : "outline"} onClick={() => setStatus(val)} className={status === val ? "bg-evergreen-950 text-white" : ""}>{label}</Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="candidate-review-stats">
        {["pending", "ambiguous", "redaction_blocked", "failed", "approved"].map(key => (
          <Card key={key} className="rounded-xl border bg-white"><CardContent className="p-4">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-400">{key.replace("_", " ")}</p>
            <p className="text-2xl font-accent font-bold text-slate-900" data-testid={`candidate-review-stat-${key}`}>{queue.stats?.[key] ?? 0}</p>
          </CardContent></Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[420px_minmax(0,1fr)] gap-6">
        <Card className="rounded-xl border bg-white shadow-sm" data-testid="candidate-review-list-card">
          <CardContent className="p-0">
            <div className="p-4 border-b border-slate-100 flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-evergreen-950" />
              <h2 className="font-heading text-base text-slate-900">Queue</h2>
              <span className="ml-auto text-xs text-slate-400">{queue.total} records</span>
            </div>
            {loading ? <div className="p-6 text-sm text-slate-500">Loading...</div> : (
              <div className="max-h-[650px] overflow-y-auto">
                {queue.items.map(item => (
                  <button key={item.anonymous_id} data-testid={`candidate-review-row-${item.anonymous_id}`} onClick={() => setSelectedId(item.anonymous_id)}
                    className={`w-full text-left p-4 border-b border-slate-100 hover:bg-slate-50 transition-colors ${selectedId === item.anonymous_id ? "bg-lime-50/60" : ""}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-slate-900">{item.name || item.anonymous_id}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{item.current_title || "Untitled"}</p>
                      </div>
                      <Badge className={item.review_reason === "redaction_blocked" ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200"}>{item.review_reason?.replace("_", " ")}</Badge>
                    </div>
                    <p className="text-xs font-mono text-slate-400 mt-2">{item.anonymous_id}</p>
                  </button>
                ))}
                {queue.items.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No records in this view.</div>}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="rounded-xl border bg-white shadow-sm" data-testid="candidate-review-detail-card">
            <CardContent className="p-6">
              {!detail ? <p className="text-sm text-slate-500">Select a candidate to review.</p> : (
                <div className="space-y-5">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div>
                      <p className="text-xs font-mono uppercase tracking-widest text-slate-400">{detail.item.anonymous_id}</p>
                      <h2 className="text-xl font-heading text-slate-900 mt-1">{detail.item.name || "Unnamed candidate"}</h2>
                      <p className="text-sm text-slate-500">{detail.processed_snapshot.current_employer_name || "Employer unavailable"}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid="candidate-review-enrich-button" variant="outline" onClick={runEnrichment} disabled={enriching}>{enriching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Run AI</Button>
                      <Button data-testid="candidate-review-save-button" variant="outline" onClick={saveReview} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save</Button>
                      <Button data-testid="candidate-review-approve-button" onClick={approveReview} disabled={saving} className="bg-lime-600 hover:bg-lime-700 text-white"><CheckCircle2 className="w-4 h-4 mr-2" />Approve</Button>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4" data-testid="candidate-review-qc-panel">
                    <div className="flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-evergreen-950" />
                      <h3 className="font-heading text-base text-slate-900">Admin QC Source Data</h3>
                    </div>
                    <SourceActions raw={detail.raw_payload} snapshot={detail.processed_snapshot} anonymousId={detail.item.anonymous_id} onCopy={copyText} />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="candidate-review-source-profile-id"><p className="text-xs font-mono uppercase tracking-widest text-slate-400">Source Profile ID</p><p className="mt-1 text-slate-700 break-all">{clean(detail.raw_payload.profile_id || detail.raw_payload.person_id)}</p></div>
                      <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="candidate-review-source-slug"><p className="text-xs font-mono uppercase tracking-widest text-slate-400">Slug</p><p className="mt-1 text-slate-700 break-all">{clean(detail.raw_payload.slug)}</p></div>
                      <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="candidate-review-source-refresh"><p className="text-xs font-mono uppercase tracking-widest text-slate-400">Last Refresh</p><p className="mt-1 text-slate-700 break-all">{clean(detail.raw_payload.last_refresh)}</p></div>
                    </div>
                  </div>

                  {(detail.item.summary_redaction_issues?.length > 0 || detail.item.processing_errors?.length > 0) && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="candidate-review-warning-box">
                      <div className="flex items-center gap-2 text-amber-800 font-medium text-sm"><ShieldAlert className="w-4 h-4" /> Review required</div>
                      <ul className="mt-2 text-xs text-amber-700 list-disc pl-5 space-y-1">
                        {[...(detail.item.processing_errors || []), ...(detail.item.summary_redaction_issues || [])].map((issue, i) => <li key={i}>{issue}</li>)}
                      </ul>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="text-xs font-mono uppercase tracking-widest text-slate-400">Title</label><Input data-testid="candidate-review-title-input" value={form.current_title || ""} onChange={e => setForm(f => ({ ...f, current_title: e.target.value }))} className="mt-2" /></div>
                    <div className="md:col-span-2"><label className="text-xs font-mono uppercase tracking-widest text-slate-400">Seniority</label><SingleSelect testId="candidate-review-seniority-select" options={SENIORITY} value={form.seniority_level || ""} onChange={v => setForm(f => ({ ...f, seniority_level: v }))} /></div>
                    <div className="md:col-span-2"><label className="text-xs font-mono uppercase tracking-widest text-slate-400">Function</label><SingleSelect testId="candidate-review-function-select" options={FUNCTIONAL_AREAS} value={form.functional_area || "Other"} onChange={v => setForm(f => ({ ...f, functional_area: v }))} /></div>
                    <div className="md:col-span-2"><label className="text-xs font-mono uppercase tracking-widest text-slate-400">Geography</label><SingleSelect testId="candidate-review-geo-select" options={GEO} value={form.geography_region || ""} onChange={v => setForm(f => ({ ...f, geography_region: v }))} /></div>
                  </div>

                  <div><label className="text-xs font-mono uppercase tracking-widest text-slate-400 block mb-2">Therapeutic Areas</label><MultiSelect testId="candidate-review-ta-select" options={THERAPEUTIC_AREAS} value={form.therapeutic_areas || []} onChange={v => setForm(f => ({ ...f, therapeutic_areas: v }))} /></div>
                  <div><label className="text-xs font-mono uppercase tracking-widest text-slate-400">Client-safe experience summary</label><Textarea data-testid="candidate-review-experience-summary" value={form.ai_experience_summary || ""} onChange={e => setForm(f => ({ ...f, ai_experience_summary: e.target.value }))} className="mt-2 min-h-[100px]" /></div>
                  <div><label className="text-xs font-mono uppercase tracking-widest text-slate-400">Client-safe intent summary</label><Textarea data-testid="candidate-review-intent-summary" value={form.intent_level10_summary || ""} onChange={e => setForm(f => ({ ...f, intent_level10_summary: e.target.value }))} className="mt-2 min-h-[100px]" /></div>
                  <div><label className="text-xs font-mono uppercase tracking-widest text-slate-400">Admin notes</label><Textarea data-testid="candidate-review-admin-notes" value={form.admin_notes || ""} onChange={e => setForm(f => ({ ...f, admin_notes: e.target.value }))} className="mt-2 min-h-[80px]" /></div>
                  <div className="space-y-4 border-t border-slate-100 pt-5" data-testid="candidate-review-source-sections">
                    <div>
                      <div className="flex items-center gap-2 mb-3"><BriefcaseBusiness className="w-4 h-4 text-evergreen-950" /><h3 className="font-heading text-base text-slate-900">Experience Timeline</h3></div>
                      <ExperienceTimeline experience={detail.raw_payload.experience || detail.raw_payload.current_experience || []} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-3"><GraduationCap className="w-4 h-4 text-evergreen-950" /><h3 className="font-heading text-base text-slate-900">Education</h3></div>
                      <EducationList education={detail.raw_payload.education || []} />
                    </div>
                    <RawJsonPanel raw={detail.raw_payload} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-xl border bg-white shadow-sm" data-testid="candidate-import-history-card">
            <CardContent className="p-0 overflow-x-auto">
              <div className="p-4 border-b border-slate-100 flex items-center gap-2"><History className="w-4 h-4 text-evergreen-950" /><h2 className="font-heading text-base text-slate-900">Import & Enrichment History</h2></div>
              <div className="min-w-[760px] text-sm">
                <div className="grid grid-cols-[110px_1.4fr_90px_90px_90px_150px] bg-slate-50">
                  {['Batch', 'Source', 'Imported', 'Approved', 'Pending', 'Last Enrichment'].map(h => <div key={h} className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">{h}</div>)}
                </div>
                {history.batches?.map(b => <div key={b.id} className="grid grid-cols-[110px_1.4fr_90px_90px_90px_150px] border-t border-slate-100" data-testid={`candidate-history-row-${b.id}`}>
                  <div className="px-4 py-3 font-mono text-xs text-slate-500">{b.id?.slice(0, 8)}</div>
                  <div className="px-4 py-3 text-slate-600 truncate">{b.source_system}</div>
                  <div className="px-4 py-3 text-slate-600">{b.raw_count ?? b.total ?? 0}</div>
                  <div className="px-4 py-3 text-lime-700 font-medium">{b.approved_count ?? 0}</div>
                  <div className="px-4 py-3 text-amber-700 font-medium">{b.pending_review_count ?? 0}</div>
                  <div className="px-4 py-3 text-slate-500 text-xs">{b.last_enriched_at ? b.last_enriched_at.slice(0, 19) : "—"}</div>
                </div>)}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}